import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Table, Tooltip, Tabs, Tag, message } from "antd";
import { FaFileAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axiosHttp from "../../appRedux/services/axiosCommon";
import Page from "../../components/Common/Page";

// --- Constants ---
const PAGE_STATUS_COLORS = {
  missing: "default",
  draft: "gold",
  published: "green",
  archived: "red",
  active: "green",
  inactive: "red",
};

const PAGE_STATUS_LABELS = {
  missing: "Missing",
  draft: "Draft",
  published: "Published",
  archived: "Archived",
  active: "Active",
  inactive: "Inactive",
};

const PAGE_STATUS_DESCRIPTIONS = {
  missing: "No editable page document exists yet.",
  draft: "Editable internally; not served by public API.",
  published: "Live on public pages.",
  archived: "Kept for recovery; hidden from public API.",
  active: "Live on public pages.",
  inactive: "Inactive and hidden.",
};

const StatusPill = ({ status }) => (
  <Tag color={PAGE_STATUS_COLORS[status] || "default"} style={{ marginRight: 0, fontWeight: 700 }}>
    {PAGE_STATUS_LABELS[status] || status}
  </Tag>
);

const PagesList = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("topic_pages");
  const [topics, setTopics] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSearchText, setPageSearchText] = useState("");
  const [platformSearchText, setPlatformSearchText] = useState("");

  const fetchTopics = async () => {
    setLoading(true);
    try {
      const response = await axiosHttp.get("/v3/admin/topics");
      if (response.data?.data || response.data?.results || Array.isArray(response.data)) {
        setTopics(response.data.data || response.data.results || response.data || []);
      } else {
        message.error("Failed to fetch topics");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to fetch topics");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatforms = async () => {
    setLoading(true);
    try {
      const response = await axiosHttp.get("/v1/admin/platforms?size=100");
      const list =
        response?.data?.results?.data ||
        response?.data?.data?.data ||
        (Array.isArray(response?.data?.results) ? response.data.results : null) ||
        (Array.isArray(response?.data?.data) ? response.data.data : null) ||
        (Array.isArray(response?.data) ? response.data : null) ||
        [];
      setPlatforms(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to fetch platform pages:", error);
      message.error("Failed to fetch platform pages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "topic_pages") {
      fetchTopics();
    } else if (activeTab === "platform_pages") {
      fetchPlatforms();
    }
  }, [activeTab]);

  const openPageEditor = (topic) => {
    if (topic.page?._id) {
      navigate(`/skill/builder/${topic.page._id}`);
    } else {
      message.error("Skill page not initialized.");
    }
  };

  const openPlatformBuilder = (platform) => {
    if (platform._id) {
      navigate(`/platforms/builder/${platform._id}`);
    } else {
      message.error("Platform ID missing.");
    }
  };

  // Only keep topics that have an initialized page (status not missing or undefined)
  const initializedTopics = useMemo(() => {
    return topics.filter((t) => {
      const status = t.page?.status;
      return status && status !== "missing";
    });
  }, [topics]);

  const pageTableData = useMemo(() => {
    const query = pageSearchText.trim().toLowerCase();
    let filtered = initializedTopics;
    if (query) {
      filtered = initializedTopics.filter(
        (topic) =>
          String(topic.name || "").toLowerCase().includes(query) ||
          String(topic.page?.slug || "").toLowerCase().includes(query)
      );
    }

    const getStatusRank = (t) => {
      const status = t.page?.status;
      switch (status) {
        case "published":
        case "active":
          return 1;
        case "draft":
          return 2;
        case "archived":
        case "inactive":
          return 3;
        default:
          return 4;
      }
    };

    return [...filtered].sort((a, b) => {
      const rankDiff = getStatusRank(a) - getStatusRank(b);
      if (rankDiff !== 0) return rankDiff;
      const dateA = new Date(a.page?.updatedAt || a.page?.updated_at || 0);
      const dateB = new Date(b.page?.updatedAt || b.page?.updated_at || 0);
      return dateB - dateA;
    });
  }, [initializedTopics, pageSearchText]);

  const platformTableData = useMemo(() => {
    const query = platformSearchText.trim().toLowerCase();
    if (!query) return platforms;
    return platforms.filter((p) => {
      return (
        String(p.name || "").toLowerCase().includes(query) ||
        String(p.code || "").toLowerCase().includes(query) ||
        String(p.verdict?.summary || "").toLowerCase().includes(query)
      );
    });
  }, [platforms, platformSearchText]);

  const pageColumns = [
    {
      title: "Topic Page",
      dataIndex: "name",
      key: "name",
      width: 260,
      render: (value, record) => (
        <div>
          <strong style={{ fontSize: 15, color: "#0f172a" }}>{value}</strong>
          {record.page?.slug && (
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              /{record.page.slug}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "pageStatus",
      width: 120,
      render: (_, record) => {
        const status = record.page?.status || "missing";
        return (
          <Tooltip title={PAGE_STATUS_DESCRIPTIONS[status]} placement="topLeft">
            <div style={{ cursor: "help", display: "inline-block" }}>
              <StatusPill status={status} />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Last Updated",
      key: "lastUpdated",
      width: 180,
      render: (_, record) => (
        <span style={{ fontSize: 13, color: "#475569" }}>
          {record.page?.updatedAt || record.page?.updated_at
            ? new Date(record.page.updatedAt || record.page.updated_at).toLocaleString()
            : "--"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 130,
      render: (_, record) => (
        <Button
          type="default"
          size="small"
          onClick={() => openPageEditor(record)}
          icon={<FaFileAlt />}
          style={{
            borderRadius: 6,
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          Edit Page
        </Button>
      ),
    },
  ];

  const platformColumns = [
    {
      title: "Platform Page",
      key: "name",
      width: 240,
      render: (_, record) => {
        const brandColor = record.brandColor || "#4f46e5";
        const initials = (record.name || "P").substring(0, 2).toUpperCase();
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                backgroundColor: brandColor,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 12,
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <div>
              <strong style={{ fontSize: 14, color: "#0f172a", display: "block" }}>
                {record.name}
              </strong>
              <span style={{ fontSize: 11, color: "#64748b" }}>/platforms/{record.code}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Verdict & Score",
      key: "verdict",
      width: 200,
      render: (_, record) => {
        const score = record.verdict?.score;
        return (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
              <Tag color="gold" style={{ fontWeight: 700, margin: 0, borderRadius: 4 }}>
                ★ {score !== undefined && score !== null ? Number(score).toFixed(1) : "8.5"} / 10
              </Tag>
            </div>
            <div
              style={{
                fontSize: 11,
                color: "#64748b",
                maxWidth: 220,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {record.verdict?.summary || record.tagline || "Decision guide & review ready"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Decision Tags",
      key: "tags",
      width: 180,
      render: (_, record) => {
        const tags = Array.isArray(record.tags) ? record.tags : [];
        if (tags.length === 0) {
          return <span style={{ color: "#94a3b8", fontSize: 12 }}>No tags assigned</span>;
        }
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {tags.slice(0, 2).map((t, idx) => (
              <Tag key={t._id || idx} color="blue" style={{ fontSize: 10, margin: 0, borderRadius: 3 }}>
                {typeof t === "object" ? t.name : t}
              </Tag>
            ))}
            {tags.length > 2 && (
              <Tag style={{ fontSize: 10, margin: 0, borderRadius: 3 }}>+{tags.length - 2}</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, record) => {
        const status = record.status || "published";
        return <StatusPill status={status} />;
      },
    },
    {
      title: "Last Updated",
      key: "lastUpdated",
      width: 160,
      render: (_, record) => (
        <span style={{ fontSize: 12, color: "#64748b" }}>
          {record.updated_at || record.updatedAt
            ? new Date(record.updated_at || record.updatedAt).toLocaleDateString()
            : "--"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 140,
      render: (_, record) => (
        <Button
          type="primary"
          ghost
          size="small"
          onClick={() => openPlatformBuilder(record)}
          icon={<FaFileAlt />}
          style={{
            borderRadius: 6,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          PageBuilder
        </Button>
      ),
    },
  ];

  const items = [
    {
      key: "topic_pages",
      label: "Topic Pages",
      children: (
        <div>
          <div className="card">
            <div className="card-body">
              <div
                className="d-flex justify-content-between align-items-start mb-3"
                style={{ gap: 16, flexWrap: "wrap" }}
              >
                <div>
                  <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>Topic Pages</h4>
                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                    Manage and edit the public-facing pages for your topics. Only initialized pages are shown here.
                  </div>
                </div>
                <div style={{ width: 400 }}>
                  <Input
                    placeholder="Search pages by topic or slug..."
                    value={pageSearchText}
                    onChange={(e) => setPageSearchText(e.target.value)}
                    style={{ borderRadius: "6px", borderColor: "#d9d9d9", height: "38px", margin: 0 }}
                  />
                </div>
              </div>
              <div style={{ width: "100%", overflowX: "auto" }}>
                <Table
                  rowKey={(record) => record._id}
                  columns={pageColumns}
                  dataSource={pageTableData}
                  loading={loading}
                  size="middle"
                  scroll={{ x: "max-content" }}
                  pagination={{ pageSize: 50 }}
                />
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "platform_pages",
      label: "Platform Pages",
      children: (
        <div>
          <div className="card">
            <div className="card-body">
              <div
                className="d-flex justify-content-between align-items-start mb-3"
                style={{ gap: 16, flexWrap: "wrap" }}
              >
                <div>
                  <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>Platform Decision Pages</h4>
                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                    Curate and manage decision guidance reviews, verdicts, and honest cost breakdowns for platforms.
                  </div>
                </div>
                <div style={{ width: 400 }}>
                  <Input
                    placeholder="Search platform pages by name, code, or verdict..."
                    value={platformSearchText}
                    onChange={(e) => setPlatformSearchText(e.target.value)}
                    style={{ borderRadius: "6px", borderColor: "#d9d9d9", height: "38px", margin: 0 }}
                  />
                </div>
              </div>
              <div style={{ width: "100%", overflowX: "auto" }}>
                <Table
                  rowKey={(record) => record._id}
                  columns={platformColumns}
                  dataSource={platformTableData}
                  loading={loading}
                  size="middle"
                  scroll={{ x: "max-content" }}
                  pagination={{ pageSize: 50 }}
                />
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "category_pages",
      label: "Category Pages",
      children: (
        <div style={{ padding: "40px 20px", textAlign: "center", background: "#fff", borderRadius: 8 }}>
          <h4 style={{ color: "#64748b" }}>Category Pages Coming Soon</h4>
          <p style={{ color: "#94a3b8" }}>The ability to manage category-specific pages will be added here.</p>
        </div>
      ),
    },
  ];

  return (
    <Page title="Pages Administration">
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="mb-3">
            <h3 className="mb-0">Pages</h3>
          </div>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={items}
            className="custom-tabs"
            style={{
              background: "#fff",
              padding: "0",
              borderRadius: "8px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
            }}
          />
        </div>
      </div>
    </Page>
  );
};

export default PagesList;
